import { DataProvider, useDataValue } from 'Simple/Data'
import _tokens from 'Simple/DesignTokens.json'
import makeDebug from 'Simple/debug.js'
import get from 'lodash/get'
import set from 'lodash/set'
import cloneDeep from 'lodash/cloneDeep'
import React, { useMemo } from 'react'

let debug = makeDebug('simple/design-tokens')

let defaultTheme = Object.keys(_tokens)[0]

export function DesignTokens({
  theme = defaultTheme,
  overrides = null,
  children,
  viewPath,
}) {
  if (!(theme in _tokens)) {
    // safeguard to avoid wrong theme values
    theme = defaultTheme
  }

  let generatedTokens = useMemo(() => {
    // Deepclone is needed since, on initial render, overrides are not set. They are set later on using the useDataChange.
    // And directly expanding the original tokens would bake in all the references. Making it not possible to set the overrides later on.
    let tempTokens = cloneDeep(_tokens)[theme]
    if (overrides !== null) {
      Object.keys(overrides).forEach((overrideKey) => {
        set(tempTokens, overrideKey, overrides[overrideKey])
      })
    }

    // recursively expand all the references in json. ex: $.colors.blue.300
    expand(tempTokens)
    return tempTokens

    function expand(obj, key = null) {
      for (let item in obj) {
        // if value is object, call the function again
        if (typeof obj[item] === 'object') {
          expand(obj[item], key ?? item)
        } else if (typeof obj[item] === 'string') {
          // if value is string, check if it contains "$"
          let reference = obj[item].split('$.')[1]
          if (reference) {
            // if yes, replace with referenced theme's value
            obj[item] = get(tempTokens, reference)
          }
        }
      }
    }
  }, [overrides, theme])

  return (
    <DataProvider
      context="design_tokens"
      value={generatedTokens}
      viewPath={viewPath}
    >
      {children}
    </DataProvider>
  )
}

export function useDesignTokenValue({ path, viewPath }) {
  let token = useDataValue({
    context: 'design_tokens',
    viewPath,
    path,
  })

  if (token === null) {
    debug({
      type: 'missing-design-token',
      viewPath,
      message: `You're missing the design token "${path}". Please add it to your project's design-tokens.json file.`,
    })
  }
  return token
}
